import type { ForwardedRef, ReactNode, RefObject } from "react"
import { useCallback, useLayoutEffect, useRef, useState } from "react"

import { cn } from "@productlane/lib/src/cn"
import { Label } from "@productlane/ui"

import type { HighlightToken } from "./policy-token-highlight"
import { NOTICE_TEXT } from "./field-notice"
import { HIGHLIGHT_METRICS } from "./highlight-metrics"
import { TokenText } from "./policy-token-highlight"

/** Vertical metrics for a field whose overlay has to wrap with it. */
export const HIGHLIGHT_VERTICAL = "py-2 leading-5"

/** Keeps password managers and spellcheckers off a field that holds a reference. */
export const PLAIN_TEXT_FIELD = {
	dir: "ltr",
	spellCheck: false,
	autoComplete: "off",
	"data-1p-ignore": "true",
	"data-lpignore": "true",
} as const

type Field = HTMLInputElement | HTMLTextAreaElement

/**
 * Keeps the token overlay pinned to the text under the caret. The field scrolls,
 * the overlay is translated by the same amount, one frame at a time.
 */
export function useHighlightOverlay<T extends Field>({
	value,
	axis,
	forwardedRef,
}: {
	value: unknown
	axis: "x" | "xy"
	forwardedRef: ForwardedRef<T>
}) {
	const fieldRef = useRef<T | null>(null)
	const overlayRef = useRef<HTMLSpanElement>(null)
	const frameRef = useRef<number | null>(null)
	const [composing, setComposing] = useState(false)

	const setRef = useCallback(
		(node: T | null) => {
			fieldRef.current = node
			if (typeof forwardedRef === "function") forwardedRef(node)
			else if (forwardedRef) forwardedRef.current = node
		},
		[forwardedRef],
	)

	const syncScroll = useCallback(() => {
		if (frameRef.current !== null) return
		frameRef.current = requestAnimationFrame(() => {
			frameRef.current = null
			const field = fieldRef.current
			const overlay = overlayRef.current
			if (!field || !overlay) return
			overlay.style.transform =
				axis === "x"
					? `translateX(${-field.scrollLeft}px)`
					: `translate(${-field.scrollLeft}px, ${-field.scrollTop}px)`
		})
	}, [axis])

	useLayoutEffect(syncScroll, [syncScroll, value])

	return {
		fieldRef,
		overlayRef,
		setRef,
		composing,
		syncProps: {
			onScroll: syncScroll,
			onSelect: syncScroll,
			onKeyUp: syncScroll,
			onCompositionStart: () => setComposing(true),
			onCompositionEnd: () => setComposing(false),
		},
	}
}

/** Label above, field in a positioned box, one line for the error or the hint. */
export function FieldShell({
	label,
	htmlFor,
	hint,
	errorMessage,
	className,
	children,
}: {
	label?: ReactNode
	htmlFor?: string
	hint?: ReactNode
	errorMessage?: ReactNode
	className?: string
	children: ReactNode
}) {
	return (
		<div className={cn("flex w-full flex-col", className)}>
			{label ? (
				<Label className="mb-1.5" htmlFor={htmlFor}>
					{label}
				</Label>
			) : null}
			<div className="relative">{children}</div>
			{errorMessage || hint ? (
				<p
					className={cn(
						"mt-1.5 min-h-4 text-xs",
						errorMessage ? NOTICE_TEXT.error : NOTICE_TEXT.muted,
					)}
				>
					{errorMessage ?? hint}
				</p>
			) : null}
		</div>
	)
}

/**
 * The colored text, drawn in front of the field so the opaque selection band the
 * field paints cannot cover the glyphs.
 */
export function HighlightOverlay({
	tokens,
	placeholder,
	composing,
	overlayRef,
	shape,
	className,
	children,
}: {
	tokens: HighlightToken[]
	placeholder?: string
	composing: boolean
	overlayRef: RefObject<HTMLSpanElement | null>
	shape: "single" | "multi"
	/** Padding that has to match the field underneath. */
	className?: string
	children?: ReactNode
}) {
	const single = shape === "single"

	return (
		<span
			aria-hidden={true}
			dir="ltr"
			className={cn(
				"pointer-events-none absolute inset-0 flex select-none overflow-hidden",
				HIGHLIGHT_METRICS,
				single ? "items-center" : cn("items-start", HIGHLIGHT_VERTICAL),
				composing && "invisible",
				className,
			)}
		>
			{tokens.length === 0 && placeholder ? (
				<span className={cn("text-tertiary", single && "truncate")}>
					{placeholder}
				</span>
			) : (
				<TokenText ref={overlayRef} tokens={tokens}>
					{children}
				</TokenText>
			)}
		</span>
	)
}
